<template>
  <div>
    <div
      v-if="socialUrls && socialUrls.length > 0"
      class="mb-0"
      v-for="(socialUrl, index) in socialUrls"
      :key="index"
    >
      <v-text-field
        :model-value="socialUrl.url"
        @update:model-value="updateUrl(index, $event)"
        :label="t(strings.urlLabel)"
        :placeholder="t(strings.urlPlaceholder)"
        variant="outlined"
        density="comfortable"
        class="mb-4"
        :rules="[urlRule]"
        @blur="validateUrl(socialUrl.url, index)"
        append-inner-icon="mdi-delete"
        @click:append-inner="removeUrl(index)"
      />
    </div>

    <v-btn
      variant="text"
      color="primary"
      prepend-icon="mdi-plus"
      @click="addUrl"
      class="mb-4"
    >
      {{ t(strings.addUrlButton) }}
    </v-btn>

    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      density="compact"
      class="mb-2"
    >
      {{ errorMessage }}
    </v-alert>
  </div>
</template>

<script setup lang="ts">
import { useReverseT } from "../i18n.ts";
import { ref, watch, onMounted } from "vue";

const { t } = useReverseT();

// Props
const props = defineProps<{
  modelValue?: Array<{ url: string }> | null;
  errorMessage?: string;
}>();

// Emits
const emit = defineEmits<{
  "update:modelValue": [value: Array<{ url: string }> | null];
}>();

// Local state
const socialUrls = ref<Array<{ url: string }>>([]);

// Validation rules
const urlRule = (v: string) => {
  if (!v) return true; // Let required rule handle empty values
  try {
    new URL(v);
    return true;
  } catch {
    return t(strings.invalidUrlError);
  }
};

// Methods
const addUrl = () => {
  socialUrls.value.push({ url: "" });
  emitUpdate();
};

const removeUrl = (index: number) => {
  socialUrls.value.splice(index, 1);
  emitUpdate();
};

const updateUrl = (index: number, url: string) => {
  if (socialUrls.value[index]) {
    socialUrls.value[index].url = url;
    emitUpdate();
  }
};

const validateUrl = (url: string, index: number) => {
  if (url && !url.startsWith("http://") && !url.startsWith("https://")) {
    // Auto-prepend https:// if no protocol
    if (socialUrls.value[index]) {
      socialUrls.value[index].url = `https://${url}`;
      emitUpdate();
    }
  }
};

const emitUpdate = () => {
  // Always emit the current state if there are any URLs in the array
  // Only emit null if the array is completely empty
  emit(
    "update:modelValue",
    socialUrls.value.length > 0 ? socialUrls.value : null,
  );
};

// Watch for prop changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue && Array.isArray(newValue)) {
      socialUrls.value = [...newValue];
    } else {
      socialUrls.value = [];
    }
  },
  { immediate: true },
);

// Emit initial value
onMounted(() => {
  emitUpdate();
});

// Strings
const strings = {
  urlLabel: "URL",
  urlPlaceholder: "https://example.com",
  addUrlButton: "Add Social URL",
  invalidUrlError: "Please enter a valid URL",
};
</script>
